import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { Link } from "react-router-dom";
import { 
  FiUsers, 
  FiTrash2, 
  FiEye, 
  FiShoppingCart,
  FiMail,
  FiPhone,
  FiUser,
  FiSearch,
  FiRefreshCw
} from 'react-icons/fi';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import "../styles/UserList.css";
import "./ProductManagement.css";

// Revenue net of refunds for one order, using the same Firestore fields the
// backend's /refund-payment route writes (order.fullyRefunded / order.refundedItems)
// - without this, a refunded order still counted its full price toward "Omsetning".
function getNetOrderRevenue(order) {
  if (order.fullyRefunded) return 0;
  const orderItems = order.items || order.products || [];
  let refundedAmount = 0;
  orderItems.forEach(item => {
    const key = `${order.orderReference}_${item.name}`;
    const refundedQty = (order.refundedItems && order.refundedItems[key]) || 0;
    refundedAmount += refundedQty * (item.price || 0);
  });
  return (order.totalPrice || 0) - refundedAmount;
}

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');

  // Fetch all users and their orders from Postgres
  useEffect(() => {
    async function fetchData() {
      try {
        // Guest checkout records live in this same table (keyed by phone, not
        // email) - they have their own page (Gjester), not the member list.
        const { data: userRows, error: usersError } = await supabase
          .from("users").select("*").eq("is_guest", false);
        if (usersError) throw usersError;
        setUsers(userRows || []);

        const userIds = (userRows || []).map(u => u.id);
        const { data: orderRows, error: ordersError } = userIds.length
          ? await supabase.from("orders").select("*, order_items(*)").in("user_id", userIds)
          : { data: [] };
        if (ordersError) throw ordersError;

        const ordersByUser = {};
        (orderRows || []).forEach(order => {
          const shaped = {
            id: order.id,
            orderReference: order.order_reference,
            totalPrice: order.total_price,
            fullyRefunded: order.fully_refunded,
            refundedItems: order.refunded_items,
            // Needed for getNetOrderRevenue's per-item refund calc - a
            // partially-refunded order was silently counting its full
            // original price toward "Omsetning" without these.
            items: (order.order_items || []).map(item => ({ name: item.name, price: item.price, quantity: item.quantity })),
          };
          if (!ordersByUser[order.user_id]) ordersByUser[order.user_id] = [];
          ordersByUser[order.user_id].push(shaped);
        });
        setOrders(ordersByUser);
      } catch (err) {
        setError("Kunne ikke hente brukere eller bestillinger: " + err.message);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Delete user - goes through the backend so everything under it (orders,
  // tickets, klippekort, coupons) and the Supabase Auth account are actually
  // removed too, not just the row itself (only the service-role key can
  // delete another user's Auth account at all).
  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm("Er du sikker på at du vil slette denne brukeren? Dette sletter all data permanent.")) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${backendUrl}/api/users/${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      setUsers(users.filter(u => u.id !== userId));

      // Remove user's orders from state
      const newOrders = { ...orders };
      delete newOrders[userId];
      setOrders(newOrders);
    } catch (err) {
      alert("Kunne ikke slette bruker: " + err.message);
    }
  };

  // Filter and sort users
  const filteredUsers = users.filter(user => 
    (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    switch (sortBy) {
      case 'name-asc': return (a.name || '').localeCompare(b.name || '');
      case 'name-desc': return (b.name || '').localeCompare(a.name || '');
      case 'date-desc': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'date-asc': return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      default: return 0;
    }
  });

  // Calculate total orders and revenue
  const totalUsers = users.length;
  const totalOrders = Object.values(orders).flat().length;
  const totalRevenue = Object.values(orders).flat().reduce((sum, order) =>
    sum + getNetOrderRevenue(order), 0
  );

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-container">
          <FiRefreshCw className="loading-spinner" />
          <p>Laster brukere...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="error-container">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-container">
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Brukeradministrasjon</h1>
          <p className="admin-subtitle">Administrer alle brukere og deres bestillinger</p>
        </div>
        <div className="admin-stats">
          <div className="stat-item">
            <FiUsers />
            <span>{totalUsers} Brukere</span>
          </div>
          <div className="stat-item">
            <FiShoppingCart />
            <span>{totalOrders} Bestillinger</span>
          </div>
          <div className="stat-item">
            <span>{totalRevenue.toFixed(0)} NOK Omsetning</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="pm-filters-section">
        <div className="pm-search-wrapper">
          <FiSearch className="pm-search-icon" />
          <input
            type="text"
            placeholder="Søk brukere etter navn, e-post eller telefon..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pm-search-input"
          />
        </div>
        <div className="pm-filter-group">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="pm-filter-select"
          >
            <option value="name-asc">Navn A–Å</option>
            <option value="name-desc">Navn Å–A</option>
            <option value="date-desc">Nyeste først</option>
            <option value="date-asc">Eldste først</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="card users-card">
        <div className="users-header">
          <h2><FiUsers style={{ marginRight: '8px', verticalAlign: 'middle' }} />Brukere</h2>
          <span className="users-count">{filteredUsers.length} brukere</span>
        </div>
        
        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>
                  <div className="th-content">
                    <FiUser />
                    <span>Navn</span>
                  </div>
                </th>
                <th>
                  <div className="th-content">
                    <FiMail />
                    <span>E-post</span>
                  </div>
                </th>
                <th>
                  <div className="th-content">
                    <FiPhone />
                    <span>Telefon</span>
                  </div>
                </th>
                <th>
                  <div className="th-content">
                    <FiShoppingCart />
                    <span>Bestillinger</span>
                  </div>
                </th>
                <th>Totalt brukt</th>
                <th>Registrert</th>
                <th>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const userOrders = orders[user.id] || [];
                const validOrders = userOrders.filter(order => order && Object.keys(order).length > 1);
                const totalSpent = validOrders.reduce((sum, order) => sum + getNetOrderRevenue(order), 0);
                
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          <FiUser />
                        </div>
                        <span className="user-name">{user.name || "Ingen navn"}</span>
                      </div>
                    </td>
                    <td>
                      <span className="user-email">{user.email || "Ingen e-post"}</span>
                    </td>
                    <td>
                      <span className="user-phone">{user.phone || "Ingen telefon"}</span>
                    </td>
                    <td>
                      {validOrders.length > 0 ? (
                        <Link
                          to={`/customer-orders/${user.id}`}
                          className="orders-link"
                        >
                          <FiShoppingCart />
                          <span>{validOrders.length} bestillinger</span>
                        </Link>
                      ) : (
                        <span className="no-orders">Ingen bestillinger</span>
                      )}
                    </td>
                    <td>
                      <span className="total-spent">
                        {totalSpent > 0 ? `${totalSpent.toFixed(0)} NOK` : "0 NOK"}
                      </span>
                    </td>
                    <td>
                      <span className="join-date">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString('no-NO')
                          : "Ukjent"
                        }
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link
                          to={`/customer-orders/${user.id}`}
                          className="view-btn"
                          title="Se bestillinger"
                        >
                          <FiEye />
                        </Link>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(user.id, user.email)}
                          title="Slett bruker"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && searchTerm && (
          <div className="no-results">
            <p>Ingen brukere funnet som samsvarer med "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
